import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
export default function AuthenticMexicoFood() {
  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100", "translate-y-0");
          entry.target.classList.remove("opacity-0", "translate-y-8");
        }
      });
    }, {
      threshold: 0.15
    });
    if (textRef.current) observer.observe(textRef.current);
    if (imageRef.current) observer.observe(imageRef.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} className="relative bg-background text-foreground overflow-hidden">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500&display=swap');`}</style>

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-72 h-72 rounded-full bg-amber-100 opacity-40 blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-red-100 opacity-30 blur-3xl translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-28 lg:py-36">
        <div className="flex flex-col items-center gap-0">

          <div ref={imageRef} className="relative w-full max-w-4xl mx-auto opacity-0 translate-y-8 transition-all duration-700 delay-100">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/985960/img/tacos-1-1.jpg" className="w-full h-auto object-cover" alt="Authentic Mexico Food" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 lg:p-16">
                <div ref={textRef} className="opacity-0 translate-y-8 transition-all duration-700 delay-300">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="block w-10 h-px bg-amber-400" />
                    <span className="text-amber-400 text-xs uppercase tracking-widest font-medium" style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }}>
                      Small Mexican Business
                    </span>
                  </div>

                  <h2 className="text-white text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6" style={{
                  fontFamily: "'Playfair Display', serif"
                }}>
                    Authentic
                    <span className="block italic text-amber-400">
                      Mexico Food
                    </span>
                  </h2>

                  <p className="text-white/85 text-base md:text-lg max-w-xl leading-relaxed mb-8" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 300
                }}>
                    We are a small Mexican business that offers delicious barbacoa, carnitas, chicharron, and birria. Our dishes are authentic, high-quality, and always fresh to satisfy your cravings
                  </p>

                  <div className="flex flex-wrap items-center gap-6">
                    <Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-base px-8 py-3 rounded-full shadow-lg hover:shadow-amber-400/40 hover:scale-105 transition-all duration-300" style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }}>
                      View Menu
                    </Button>

                    <div className="flex gap-5">
                      {["Barbacoa", "Carnitas", "Birria"].map(item => <span key={item} className="text-white/60 text-sm border-b border-white/30 pb-px hover:text-amber-400 hover:border-amber-400 transition-colors duration-200 cursor-default" style={{
                      fontFamily: "'DM Sans', sans-serif"
                    }}>
                          {item}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full border-2 border-amber-400/40 pointer-events-none" />
            <div className="absolute -bottom-6 -left-6 w-16 h-16 rounded-full bg-amber-400/20 pointer-events-none" />
          </div>

          <div className="mt-12 flex gap-8 md:gap-16 flex-wrap justify-center">
            {[{
            label: "Authentic Recipes",
            desc: "Traditional techniques"
          }, {
            label: "Always Fresh",
            desc: "Made daily"
          }, {
            label: "High Quality",
            desc: "Premium ingredients"
          }].map(item => <div key={item.label} className="text-center group cursor-default">
                <p className="text-foreground font-bold text-sm md:text-base group-hover:text-amber-600 transition-colors duration-200" style={{
              fontFamily: "'Playfair Display', serif"
            }}>
                  {item.label}
                </p>
                <p className="text-muted-foreground text-xs md:text-sm mt-1" style={{
              fontFamily: "'DM Sans', sans-serif"
            }}>
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}